import { defineElement, FC, onDidLoad, useElement, useListen, useProp } from '@atomify/hooks';
import { inviewObserver } from '@utilities/inview';
import { Breakpoints, breakpoints, getCurrentBreakpoint, isMobile } from '@utilities/media-query';
import EmblaCarousel, { EmblaCarouselType } from 'embla-carousel';

import { autoPlay } from './autoPlay';

const INIT_SLIDE_HOOK = 'js-hook-slider-slide-to-here';

const SliderProgress: FC<{ inView: boolean }> = ({ element }) => {
    const [autoSlide] = useProp<boolean>('autoSlide', false);
    const [autoSlideTimeout] = useProp<number>('autoSlideTimeout', 3000);
    const [sliderIfNotGreaterThan] = useProp<Breakpoints | null>('sliderIfNotGreaterThan', null);
    const [slidesToScroll] = useProp<number>('slidesToScroll', 1);
    const [, setInView, watchInView] = useProp<boolean>('inView', false);

    const slider = useElement<HTMLElement>('[js-hook-slider-progress]');
    const sliderFooter = useElement<HTMLDivElement>('[js-hook-slider-progress-footer]');
    const sliderProgress = useElement<HTMLElement>('[js-hook-slider-progress-progress]');
    const sliderPrevious = useElement<HTMLElement>('[js-hook-slider-progress-previous]');
    const sliderNext = useElement<HTMLElement>('[js-hook-slider-progress-next]');

    let embla: EmblaCarouselType | undefined;
    let slideNodes: HTMLElement[];
    let autoPlayer: ReturnType<typeof autoPlay> | undefined;
    let currentSlideIndex: number;
    let initSlideIndex: number | null = null;

    onDidLoad(componentDidLoad);
    useListen(sliderNext, 'click', nextSlide);
    useListen(sliderPrevious, 'click', previousSlide);

    function nextSlide() {
        embla?.scrollNext();
    }

    function previousSlide() {
        embla?.scrollPrev();
    }

    function fillSliderProgress() {
        if (!embla || !sliderProgress.current) return;

        const scrollProgress = embla.scrollProgress();
        const offsetFix = (100 / slideNodes.length) * (1 - scrollProgress);
        const sliderPercentage = scrollProgress * 100 + offsetFix;
        sliderProgress.current.style.transform = `translate(${sliderPercentage}%, -50%)`;
    }

    function setSelectedState() {
        if (!embla) return;

        const slides = isMobile() ? 1 : slidesToScroll;

        const selected = embla.selectedScrollSnap() * slides;
        const slidesLeft = slideNodes.length - selected - slides;

        if (slidesLeft <= 0) {
            sliderNext.current?.classList.add('slider-progress__control--is-disabled');
            sliderPrevious.current?.classList.remove('slider-progress__control--is-disabled');
        } else if (selected === 0) {
            sliderNext.current?.classList.remove('slider-progress__control--is-disabled');
            sliderPrevious.current?.classList.add('slider-progress__control--is-disabled');
        } else {
            sliderNext.current?.classList.remove('slider-progress__control--is-disabled');
            sliderPrevious.current?.classList.remove('slider-progress__control--is-disabled');
        }
    }

    function select(index: number) {
        if (currentSlideIndex !== index) {
            currentSlideIndex = index;
            embla?.scrollTo(index);
        }
    }

    function addAutoPlay() {
        if (embla) {
            autoPlayer = autoPlay(embla, autoSlideTimeout);
            autoPlayer.play();
        }
    }

    watchInView((inView) => {
        if (inView && initSlideIndex) {
            embla?.scrollTo(initSlideIndex);
            initSlideIndex = null;
        }
    });

    function checkKey(e: KeyboardEvent) {
        if (element.inView) {
            if (e.key == 'ArrowLeft') {
                previousSlide();
            } else if (e.key == 'ArrowRight') {
                nextSlide();
            }
        }
    }

    function initSlider() {
        if (!slider.current) return;

        embla = EmblaCarousel(slider.current, {
            selectedClass: 'slider-progress__cell--is-selected',
            align: 'start',
            slidesToScroll: isMobile() ? 1 : slidesToScroll,
        });

        if (!embla) return;
        slideNodes = embla.slideNodes();

        // Initialize progress when there are more than 1 item.
        if (slideNodes.length > 1) {
            sliderFooter.current?.classList.add('slider-progress__footer--has-items');
        }

        initSlideIndex = slideNodes.findIndex((slide) => slide.hasAttribute(INIT_SLIDE_HOOK));

        if (autoSlide) {
            addAutoPlay();
        }

        fillSliderProgress();
        embla.on('scroll', () => {
            fillSliderProgress();
        });

        embla.on('select', () => {
            setSelectedState();
        });

        if (autoPlayer) {
            embla.on('pointerDown', () => {
                autoPlayer?.stop();
            });

            embla.on('pointerUp', () => {
                autoPlayer?.play();
            });
        }

        slideNodes.forEach((slideNode, index) => {
            slideNode.addEventListener('click', () => {
                select(index);
            });
        });
    }

    function destroySlider() {
        if (embla) {
            embla.destroy();
            embla = undefined;
        }
    }

    function showAsSlider() {
        if (sliderIfNotGreaterThan) {
            const currentBreakpoint = getCurrentBreakpoint();

            const currentBreakpointIndex = Object.keys(breakpoints).indexOf(
                currentBreakpoint ? currentBreakpoint : '',
            );

            const maximumBreakpointIndex = Object.keys(breakpoints).indexOf(
                sliderIfNotGreaterThan ? sliderIfNotGreaterThan : '',
            );

            return currentBreakpointIndex < maximumBreakpointIndex;
        }

        return true;
    }

    function setupSlider() {
        if (showAsSlider() && !embla) {
            initSlider();
        } else {
            destroySlider();
        }
    }

    function componentDidLoad() {
        if (!slider.current) return;
        setupSlider();

        window.addEventListener('keydown', checkKey);
        inviewObserver.observe(slider.current, setInView);
    }
};

SliderProgress.props = {
    autoSlide: {
        type: Boolean,
    },
    autoSlideTimeout: {
        type: Number,
    },
    sliderIfNotGreaterThan: {
        type: String,
    },
    slidesToScroll: {
        type: Number,
        reflectToAttr: true,
    },
    inView: {
        type: Boolean,
    },
};

defineElement('bpd-slider-progress', SliderProgress);
